import { create } from "zustand";
import api from "../api/api";

type Profile = {
  name: string;
  email: string;
  ic_number: string;
  birthday: string;
  profile_pic: string | null;
  phone: string;
};

type Student = {
  birthday?: string | null;
  email?: string | null;
  id: number;
  is_active?: boolean | null;
  name: string | null;
  payment_status?: string | null;
  profile_pic?: string | null;
  student_standard?: string | null;
};

interface Voucher {
  id: string;
  type: string;
  amount: string;
  terms: string;
  academy: string;
  description: string;
  recommended?: boolean;
}

interface ProfileStore {
  // util
  loading: boolean;
  setLoading: (loading: boolean) => void;
  logout: () => void;

  // course related
  courses: [];
  setCourses: (data: []) => void;
  selectedStudent: null | Student;
  setSelectedStudent: (data: Student | null) => void;
  lastVisitedCourseId: string | null;
  setLastVisitedCourseId: (path: string | null) => void;
  clearLastVisitedCourse: () => void;

  // Academy's Branch Course usage
  selectedBranchName: null | string;
  setSelectedBranchName: (data: string | null) => void;

  // user profile related
  profile: Profile | null;
  fetchProfile: () => Promise<void>;
  students: [] | Student[];
  setStudents: (data: Student[]) => void;
  fetchStudents: () => Promise<void>;
  fetchStudentsRefresh: () => Promise<void>;
  homePageShow: string;
  hasStudentWithSchoolId: boolean;
  setHomePageShow: (data: "school" | "academy" | "explore" | "") => void;
  setProfile: (partial: Partial<Profile>) => void;

  // cart edit
  cartEdit: any;
  setCartEdit: (data: any) => void;

  // attendance
  attendanceData: {
    studentName: string;
    studentId: number;
    eventId: number;
  } | null;
  setAttendanceData: (
    data: { studentName: string; studentId: number; eventId: number } | null,
  ) => void;

  // vouchers
  selectedVouchers: Voucher[];
  setSelectedVouchers: (vouchers: Voucher[]) => void;
  toggleVoucher: (voucher: Voucher) => void;

  // login modal
  showLoginModal: boolean;
  setShowLoginModal: (show: boolean) => void;

  // guidance flow for no students
  showGuidanceFlow: boolean;
  guidanceStep:
    | "profile"
    | "add-child"
    | "search-academy"
    | "type-in-course-code"
    | "after-first-payment"
    | null;
  setShowGuidanceFlow: (show: boolean) => void;
  setGuidanceStep: (
    step:
      | "profile"
      | "add-child"
      | "search-academy"
      | "type-in-course-code"
      | "after-first-payment"
      | null,
  ) => void;

  selectedHomeworkStudent: null | Student;
  setSelectedHomeworkStudent: (data: Student | null) => void;
}

export const useProfileStore = create<ProfileStore>((set) => ({
  // util
  loading: false,
  setLoading: (loading) => {
    set({ loading });
  },
  logout: () => {
    localStorage.removeItem("parent_token");
    localStorage.removeItem("parent_user");
    localStorage.removeItem("selectedStudent");
  },

  // course related
  courses: [],
  setCourses: (data) => set({ courses: data }),
  selectedStudent: null,
  setSelectedStudent: (data: Student | null) => {
    console.log("setSelectedStudent", data);
    set({ selectedStudent: data });
  },
  lastVisitedCourseId: null,
  setLastVisitedCourseId: (path) => set({ lastVisitedCourseId: path }),
  clearLastVisitedCourse: () => set({ lastVisitedCourseId: null }),

  // Academy's Branch Course usage
  selectedBranchName: null,
  setSelectedBranchName: (data: string | null) => {
    set({ selectedBranchName: data });
  },

  // user profile related
  profile: null,
  students: [],
  setStudents: (data: Student[]) => set({ students: data }),
  fetchProfile: async () => {
    console.log("fetch profile");
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}/user/profile`,
      );
      set({ profile: res.data.data });
    } catch (err) {
      console.error("Failed to fetch profile:", err);
    }
  },
  hasStudentWithSchoolId: false,
  fetchStudents: async () => {
    console.log("fetch students");
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}/student`,
      );
      const students = res.data.result.data;

      // check if any students have school_id
      let hasStudentWithSchoolId = false;
      let studentWithSchoolId = null;

      if (students.length > 0) {
        for (let student of students) {
          if (student.school_id) {
            hasStudentWithSchoolId = true;
            studentWithSchoolId = student;
            break;
          }
        }
      } else {
        set({ showGuidanceFlow: true });
        set({ guidanceStep: "profile" });
      }

      // set homePageShow based on whether any student has school_id
      if (hasStudentWithSchoolId) {
        set({ hasStudentWithSchoolId: true });
        set({
          homePageShow: "school",
          selectedStudent: studentWithSchoolId,
        });
      } else {
        api
          .get(
            `${process.env.REACT_APP_API_BASE_URL}/parent/home/search?mode=academy`,
          )
          .then((res) => {
            console.log("res", res.data.data);
            if (res.data.data.items.length == 0) {
              set({ homePageShow: "explore" });
            } else {
              set({ homePageShow: "academy" });
            }
          })
          .catch((err) => {
            console.log("err", err);
            set({ homePageShow: "explore" }); // fallback to explore on error
          })
          .finally(() => {
            set({ loading: false });
          });
      }
      set({ students: students });
      return students;
    } catch (err) {
      console.error("Failed to fetch students:", err);
      return [];
    }
  },
  fetchStudentsRefresh: async () => {
    console.log("fetch students refersh");
    try {
      const res = await api.get(
        `${process.env.REACT_APP_API_BASE_URL}/student`,
      );
      const students = res.data.result.data;

      set({ showGuidanceFlow: false });
      set({ guidanceStep: "profile" });
      set({ students: students });
      return students;
    } catch (err) {
      console.error("Failed to fetch students:", err);
      return [];
    }
  },
  homePageShow: "",
  setHomePageShow: (data: "school" | "academy" | "explore" | "") =>
    set({ homePageShow: data }),
  setProfile: (partial) => {
    set((state) => ({
      profile: state.profile ? { ...state.profile, ...partial } : null,
    }));
  },

  // cart edit
  cartEdit: null,
  setCartEdit: (data) => set({ cartEdit: data }),

  // attendance
  attendanceData: null,
  setAttendanceData: (data) => set({ attendanceData: data }),

  // vouchers
  selectedVouchers: [],
  setSelectedVouchers: (vouchers) => set({ selectedVouchers: vouchers }),
  toggleVoucher: (voucher) => {
    set((state) => ({
      selectedVouchers: state.selectedVouchers.find(
        (v: Voucher) => v.id === voucher.id,
      )
        ? state.selectedVouchers.filter((v: Voucher) => v.id !== voucher.id)
        : [...state.selectedVouchers, voucher],
    }));
  },

  // login modal
  showLoginModal: false,
  setShowLoginModal: (show) => set({ showLoginModal: show }),

  // guidance flow for no students
  showGuidanceFlow: false,
  setShowGuidanceFlow: (show) => set({ showGuidanceFlow: show }),
  guidanceStep: null,
  setGuidanceStep: (step) => set({ guidanceStep: step }),

  selectedHomeworkStudent: null,
  setSelectedHomeworkStudent: (data: Student | null) =>
    set({ selectedHomeworkStudent: data }),
}));
